import type {
  UnitSourceApiResponse,
  UnitsClientList,
  TransformedUnit,
  SortBy,
  ResultsMode,
  TrackingEventWithUnits,
  Filters,
  useTrackingEvents,
} from "@superbright/indexeddb-orm";
import type { UseMutationResult } from "@tanstack/react-query";

export type { ResultsMode };
export type TrackingEventFns = ReturnType<typeof useTrackingEvents>;

import type {
  FiltersResponse,
  ContactBuildingPayload,
  ContactBuildingResponse,
} from "api/schemas";
import type { SortOption } from "features/results/components/SortDropdown/SortDropdown";

export type ResultsTab = "search" | "favorites";
export const RESULTS_TABS = ["search", "favorites"] as const;
export const FAVORITES_TAB: ResultsTab = "favorites";

// Base types
export type UnitsClientListResponse = UnitsClientList<UnitSourceApiResponse>;
export type ResultUnit = TransformedUnit<UnitSourceApiResponse>;

export interface ListingsMessaging {
  variant: string;
  headline: string;
  matched_dimensions: string[];
  unmatched_dimensions: string[];
  active_dimensions: string[];
}

export interface ClosestSectionClient {
  dimension: string;
  headline: string;
  total: number;
  hits: ResultUnit[];
}

export interface ListingsTierClient {
  total: number;
  hits: ResultUnit[];
  matchTier?: string;
  sections?: ClosestSectionClient[];
}

/** Listings API response after client transform (`all` + `best_fits` + `closest`). */
export interface ListingsClientResponse {
  messaging: ListingsMessaging | null;
  all: ListingsTierClient;
  bestFits: ListingsTierClient;
  closest: ListingsTierClient;
}

// Analytics context type
export interface AnalyticsContext {
  propertyId: string;
  propertySlug: string;
  propertyName: string;
}

// Main component props interface
export interface ResultsViewProps {
  data?: ListingsClientResponse | null;
  filtersData?: FiltersResponse | null;
  activeTab: ResultsTab;
  setActiveTab: (tab: ResultsTab) => void;
  resultsMode: ResultsMode | "loading";
  setResultsMode: (mode: ResultsMode | "loading") => Promise<void> | void;
  filters: Filters;
  isUnitsLoading: boolean;
  limit: number;
  setLimit: (fn: (prev: number) => number) => void;
  sortBy: SortBy;
  setSortBy: (value: SortBy) => Promise<void> | void;
  sortByOptions: SortOption[];
  propertySlug?: string;
  handleContactSubmit: (payload: Partial<ContactBuildingPayload>) => void;
  setTrackingEventWithUnits: (event: TrackingEventWithUnits | null) => void;
  tourContactedOn: string | null;
  /** Favorites known server-side, regardless of the current filter */
  contactMutation: UseMutationResult<
    ContactBuildingResponse,
    unknown,
    ContactBuildingPayload,
    unknown
  >;
}

// UnitCardWithState component props
export interface UnitCardWithStateProps {
  unit: ResultUnit;
  fallbackId: string;
  analyticsContext: AnalyticsContext | null;
  visibleHitsCount: number;
  trackClickUnit: TrackingEventFns["trackClickUnit"];
  trackHoverUnit: TrackingEventFns["trackHoverUnit"];
  trackClickUnitFavorites: TrackingEventFns["trackClickUnitFavorites"];
  trackUnitUnfavorited: TrackingEventFns["trackUnitUnfavorited"];
}
